import { useEffect, useState, useRef } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import SaveMoney from "../images/save-money .svg";
import FamilyIcon from "../images/family 1.svg";
import LocationIcon from "../images/location2.svg";
import DonationBox from "../images/donationBox.svg";
import CallIcon from "../images/call.svg";
import CustomDropdown from "./CustomSelect";
import { apiClient } from "../lib/apiClient";
import { Check } from "lucide-react";
import { toast } from "react-toastify";

// function Connector({ filled }) {
//   return (
//     <div
//       style={{
//         position: "relative",
//         width: 3,
//         flex: 1,
//         background: "#E2E8F0",
//         borderRadius: 9999,
//         overflow: "hidden",
//         margin: "6px 0",
//       }}
//     >
//       <motion.div
//         style={{
//           position: "absolute",
//           top: 0,
//           left: 0,
//           right: 0,
//           background: "linear-gradient(180deg, #F59E0B 0%, #FBBF24 100%)",
//           borderRadius: 9999,
//         }}
//         initial={{ height: "0%" }}
//         animate={{ height: filled ? "100%" : "0%" }}
//         transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1], delay: 0.1 }}
//       />
//       {filled && (
//         <motion.div
//           style={{
//             position: "absolute",
//             left: "50%",
//             transform: "translateX(-50%)",
//             width: 7,
//             height: 7,
//             borderRadius: "50%",
//             background: "#FBBF24",
//             boxShadow: "0 0 8px 3px rgba(251,191,36,0.7)",
//           }}
//           initial={{ top: "0%" }}
//           animate={{ top: "calc(100% - 7px)" }}
//           transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1], delay: 0.1 }}
//         />
//       )}
//     </div>
//   );
// }

/* ─────────────────────────────────────────────────────────────────────
   STEP CIRCLE
   Active  → number blinks + ring pulse radiates, icon toggles every 2s
   Completed → number shown, filled blue, no blink
   Future  → grey outline (only shown once that step is reached)
───────────────────────────────────────────────────────────────────── */
function StepCircle({ stepId, isActive, isCompleted, icon, showIcon, id }) {
  const filled = isActive || isCompleted;
  return (
    <div
      id={id} 
      className="relative w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
      style={{
        background: filled
          ? "linear-gradient(139.34deg,#036EA4 12.34%,#06A7CA 88.3%)"
          : "white",
        border: filled ? "none" : "2px solid #CBD5E1",
        boxShadow: filled
          ? "0 4px 16px rgba(3,110,164,0.35)"
          : "0 1px 4px rgba(0,0,0,0.08)",
        transition: "background 0.4s, box-shadow 0.4s",
      }}
    >
      <AnimatePresence mode="wait">
        {isActive && showIcon ? (
          <motion.img
            key="icon"
            src={icon}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            className="w-5 h-5 invert brightness-0"
          />
        ) : (
          <motion.span
            key="num"
            initial={{ opacity: 0 }}
            animate={
              isActive
                ? {
                    opacity: [1, 0.2, 1],
                    transition: {
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    },
                  }
                : { opacity: 1 }
            }
            exit={{ opacity: 0 }}
            className="text-sm font-bold select-none"
            style={{ color: filled ? "white" : "#94A3B8" }}
          >
            {stepId}
          </motion.span>
        )}
      </AnimatePresence>

      {/* radiating ring only on active */}
      {isActive && (
        <motion.span
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ border: "2px solid #06A7CA" }}
          animate={{ scale: [1, 1.55, 1], opacity: [0.7, 0, 0.7] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   STEP ROW  – circle vertically centred to its card + card
───────────────────────────────────────────────────────────────────── */
function StepRow({
  stepId,
  isActive,
  isCompleted,
  icon,
  showIcon,
  children,
  animateIn,
  circleRef,
}) {
  const inner = (
    <div className="flex gap-5 items-stretch">
      {/* LEFT col: circle centred to card height */}
      <div
        className="hidden md:flex w-10 flex-shrink-0 items-center justify-center"
        ref={circleRef}
      >
        <StepCircle
          id={`step-circle-${stepId}`}
          stepId={stepId}
          isActive={isActive}
          isCompleted={isCompleted}
          icon={icon}
          showIcon={showIcon}
        />
      </div>

      {/* RIGHT: card */}
      <div className="flex-1 min-w-0">
        {/* mobile: circle above card */}
        <div className="flex md:hidden items-center gap-3 mb-3">
          <StepCircle
            stepId={stepId}
            isActive={isActive}
            isCompleted={isCompleted}
            icon={icon}
            showIcon={showIcon}
          />
          <div className="h-px flex-1 bg-slate-100" />
        </div>
        <div
          className="bg-white/85 backdrop-blur-md border border-blue-50 rounded-2xl p-6 md:p-8"
          style={{
            boxShadow:
              "0 6px 28px rgba(3,110,164,0.09), 0 1px 4px rgba(0,0,0,0.05)",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  if (!animateIn) return inner;

  return (
    <motion.div
      initial={{ opacity: 0, y: 32, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
    >
      {inner}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   CONNECTOR ROW – sits between two StepRows
───────────────────────────────────────────────────────────────────── */
// function ConnectorRow({ filled, height = 220 }) {
//   return (
//     <div className="flex gap-5" style={{ height }}>
//       <div
//         className="hidden md:flex flex-col flex-shrink-0 items-center"
//         style={{ width: 40 }}
//       >
//         <Connector filled={filled} />
//       </div>
//       <div
//         className="flex md:hidden flex-col flex-shrink-0 items-center"
//         style={{ width: 40, paddingLeft: 12 }}
//       >
//         <Connector filled={filled} />
//       </div>
//       <div className="flex-1" />
//     </div>
//   );
// }
function ProgressLine({ currentStep, containerRef }) {
  const [lineTop, setLineTop] = useState(0);
  const [lineHeight, setLineHeight] = useState(0);
  const [filledHeight, setFilledHeight] = useState(0);

  useEffect(() => {
    if (currentStep < 2) return;

    const measure = () => {
      const c1 = document.getElementById("step-circle-1");
      const cN = document.getElementById(`step-circle-${currentStep}`);
      const container = containerRef?.current;
      if (!c1 || !cN || !container) return;

      const containerTop = container.getBoundingClientRect().top;
      const c1Top = c1.getBoundingClientRect().top - containerTop + 20; // +20 = circle center (40px/2)
      const cNTop = cN.getBoundingClientRect().top - containerTop + 20;

      setLineTop(c1Top);
      setLineHeight(cNTop - c1Top);
      setFilledHeight(cNTop - c1Top);
    };

    measure();
    const t = setTimeout(measure, 600); // re-measure after card animates in
    return () => clearTimeout(t);
  }, [currentStep]);

  if (currentStep === 1) return null;


  return (
    <div
      className="absolute hidden md:block"
      style={{
        left: 19,
        top: lineTop,
        width: 3,
        height: lineHeight,
        background: "#E2E8F0",
        borderRadius: 9999,
        zIndex: 0,
        overflow: "hidden",
      }}
    >
      {/* filled amber bar */}
      <motion.div
        style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          background: "linear-gradient(180deg, #F59E0B 0%, #FBBF24 100%)",
          borderRadius: 9999,
        }}
        initial={{ height: 0 }}
        animate={{ height: filledHeight }}
        transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
      />
      {/* glowing dot */}
      <motion.div
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          width: 7, height: 7,
          borderRadius: "50%",
          background: "#FBBF24",
          boxShadow: "0 0 8px 3px rgba(251,191,36,0.7)",
        }}
        initial={{ top: 0 }}
        animate={{ top: Math.max(0, filledHeight - 7) }}
        transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
      />
    </div>
  );
}
/* ─────────────────────────────────────────────────────────────────────
   NEXT BUTTON
───────────────────────────────────────────────────────────────────── */
function NextBtn({ label, onClick, disabled }) {
  return (
    <div className="flex justify-end mt-6">
      <button
        className="font-montserrat bg-[#036EA4] hover:bg-[#005f8d] text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed btn-hover-animate"
        onClick={onClick}
        disabled={disabled}
      >
        {label} <ChevronRight size={16} />
      </button>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═════════════════════════════════════════════════════════════════════ */
export default function StartIn2Minutes() {
  const { t,i18n } = useTranslation();
  const containerRef = useRef(null);
  const circleRefs = {
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
    4: useRef(null),
    5: useRef(null),
  };

  const [isStarted, setIsStarted] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [firstStep, setFirstStep] = useState(false);
  const [secondStep, setSecondStep] = useState(false);
  const [thirdStep, setThirdStep] = useState(false);
  const [fourthStep, setFourthStep] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [errors, setErrors] = useState({ name: "", email: "", phone: "",birthDate:"" });
  const [formData, setFormData] = useState({
    incomeRange: "",
    livingSituation: "",
    canton: "",
    pillar3a: "",
    contact: { name: "", phone: "", email: "",birthDate:"" },
    termsAccepted: false,
  });

  const sectionRefs = {
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
    4: useRef(null),
    5: useRef(null),
  };

  /* icon toggle every 2 s */
  useEffect(() => {
    const id = setInterval(() => setShowIcon((p) => !p), 2000);
    return () => clearInterval(id);
  }, []);
console.log(i18n.language) // "de" ya "de-DE" ?
  /* external trigger */
  useEffect(() => {
    const h = () => setIsStarted(true);
    window.addEventListener("startQuiz", h);
    return () => window.removeEventListener("startQuiz", h);
  }, []);

  /* scroll lock on success */
  useEffect(() => {
    document.body.style.overflow = isSubmitted ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isSubmitted]);

  /* scroll new step into view */
  useEffect(() => {
    if (currentStep > 1 && sectionRefs[currentStep]?.current) {
      setTimeout(() => {
        sectionRefs[currentStep].current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 150);
    }
  }, [currentStep]);
  // ✅ Fix 1 — validateDateOfBirth mein order theek karo
const validateDateOfBirth = (dob) => {
  if (!dob) return t("Date of birth is required");

  const birthDate = new Date(dob);
  const today = new Date();

  if (isNaN(birthDate.getTime())) return t("Invalid date");
  if (birthDate >= today) return t("Date of birth must be in the past");

  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const actualAge =
    monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ? age - 1
      : age;

  if (actualAge > 120) return t("Please enter a valid date of birth");
  if (actualAge > 60) return t("Persons over 60 are not eligible for insurance policy changes"); // ✅ pehle 60 check
  if (actualAge < 18) return t("You must be at least 18 years old");

  return null;
};
  /* validators */
  const validateName = (v) => (v.trim() ? "" : t("name_required"));
  const validateEmail = (v) => {
    const s = v.trim();
    if (!s) return t("email_required");
    if (!s.includes("@") || !s.includes(".")) return t("email_invalid");
    return "";
  };
  const validatePhone = (v) => {
    const c = v.trim().replace(/[\s\-\(\)\+\/]/g, "");
    if (!c) return t("phone_required");
    if (c.length < 9 || c.length > 12) return t("phone_length");
  };
const isStep5Valid = () =>
  formData.contact.name.trim() &&
  formData.contact.email.trim() &&
  formData.contact.phone.trim() &&
  formData.contact.birthDate.trim() &&
  !validateDateOfBirth(formData.contact.birthDate) && // ✅ error na ho
  formData.termsAccepted;
const handleSubmit = async () => {
  try {
    const response = await apiClient.post("lead", formData);
   

    if (!response.success) {
      throw new Error(response.message || "Übermittlung fehlgeschlagen.");
    }

    // Reset all state back to step 1
    setCurrentStep(1);
    setFirstStep(false);
    setSecondStep(false);
    setThirdStep(false);
    setFourthStep(false);

    setFormData({
      incomeRange: "",
      livingSituation: "",
      canton: "",
      pillar3a: "",
      contact: { name: "", phone: "", email: "",birthDate:"" },
      termsAccepted: false,
    });

    // setErrors({ name: "", email: "", phone: "" });

  } catch (e) {
    console.error(e);

    toast.error(
      e?.response?.data?.message || 
      e?.message || 
      "Übermittlung fehlgeschlagen."
    );
  }
};

  /* data */
  const incomeOptions = [
    "Under CHF 50,000",
    "CHF 50,000 – 120'000",
    "CHF 120'000 – 180'000",
    "Over CHF 180'000",
  ];
  const livingOptions = [
    {
      key: "Ledig, ohne Kinder",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="mask0_879_34424"
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="40"
            height="40"
          >
            <rect width="40" height="40" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_879_34424)">
            <path
              d="M15.8767 17.7767C14.7367 16.637 14.1667 15.2626 14.1667 13.6534C14.1667 12.0445 14.7367 10.6701 15.8767 9.53007C17.0167 8.39007 18.3911 7.82007 20 7.82007C21.6089 7.82007 22.9833 8.39007 24.1233 9.53007C25.2633 10.6701 25.8333 12.0445 25.8333 13.6534C25.8333 15.2626 25.2633 16.637 24.1233 17.7767C22.9833 18.9167 21.6089 19.4867 20 19.4867C18.3911 19.4867 17.0167 18.9167 15.8767 17.7767ZM7.5 32.1792V28.4738C7.5 27.6577 7.72167 26.9019 8.165 26.2063C8.60833 25.5108 9.20083 24.976 9.9425 24.6022C11.5897 23.7947 13.2515 23.189 14.9279 22.7851C16.6043 22.3812 18.295 22.1792 20 22.1792C21.705 22.1792 23.3957 22.3812 25.0721 22.7851C26.7485 23.189 28.4103 23.7947 30.0575 24.6022C30.7992 24.976 31.3917 25.5108 31.835 26.2063C32.2783 26.9019 32.5 27.6577 32.5 28.4738V32.1792H7.5ZM10 29.6792H30V28.4738C30 28.1363 29.9022 27.8238 29.7067 27.5363C29.5111 27.2491 29.2457 27.0147 28.9104 26.833C27.4743 26.1258 26.0101 25.5899 24.5179 25.2255C23.0254 24.8613 21.5194 24.6792 20 24.6792C18.4806 24.6792 16.9746 24.8613 15.4821 25.2255C13.9899 25.5899 12.5257 26.1258 11.0896 26.833C10.7543 27.0147 10.4889 27.2491 10.2933 27.5363C10.0978 27.8238 10 28.1363 10 28.4738V29.6792ZM22.3542 16.0076C23.0069 15.3548 23.3333 14.5701 23.3333 13.6534C23.3333 12.7367 23.0069 11.952 22.3542 11.2992C21.7014 10.6465 20.9167 10.3201 20 10.3201C19.0833 10.3201 18.2986 10.6465 17.6458 11.2992C16.9931 11.952 16.6667 12.7367 16.6667 13.6534C16.6667 14.5701 16.9931 15.3548 17.6458 16.0076C18.2986 16.6603 19.0833 16.9867 20 16.9867C20.9167 16.9867 21.7014 16.6603 22.3542 16.0076Z"
              fill="currentColor"
            />
          </g>
        </svg>
      ),
    },

    {
      key: "Ledig, mit Kindern",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="mask0_904_41058"
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="40"
            height="40"
          >
            <rect width="40" height="40" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_904_41058)">
            <path
              d="M9.49663 8.93246C8.89218 8.32801 8.58996 7.60482 8.58996 6.76287C8.58996 5.92093 8.89218 5.1976 9.49663 4.59287C10.1014 3.98815 10.8247 3.68579 11.6666 3.68579C12.5086 3.68579 13.2319 3.98815 13.8366 4.59287C14.4413 5.1976 14.7437 5.92093 14.7437 6.76287C14.7437 7.60482 14.4413 8.32801 13.8366 8.93246C13.2319 9.53718 12.5086 9.83954 11.6666 9.83954C10.8247 9.83954 10.1014 9.53718 9.49663 8.93246ZM28.045 18.5895C27.4039 18.5895 26.8536 18.3626 26.3941 17.9087C25.9347 17.4545 25.705 16.9016 25.705 16.25C25.705 15.5875 25.9347 15.0319 26.3941 14.5833C26.8536 14.1347 27.4039 13.9104 28.045 13.9104C28.7072 13.9104 29.2627 14.1347 29.7116 14.5833C30.1602 15.0319 30.3845 15.5875 30.3845 16.25C30.3845 16.9016 30.1602 17.4545 29.7116 17.9087C29.2627 18.3626 28.7072 18.5895 28.045 18.5895ZM8.52579 36.25V24.9358H5.89746V15.032C5.89746 14.2009 6.19177 13.4909 6.78038 12.902C7.36899 12.3134 8.07899 12.0191 8.91038 12.0191H13.8462C14.4359 12.0191 14.9675 12.1704 15.4408 12.4729C15.9141 12.7751 16.2768 13.1901 16.5287 13.7179L21.5895 24.362L24.3558 21.1795C24.5672 20.9104 24.8284 20.6998 25.1395 20.5479C25.4504 20.3962 25.782 20.3204 26.1345 20.3204H30.2241C30.8547 20.3204 31.3916 20.542 31.835 20.9854C32.2783 21.429 32.5 21.9659 32.5 22.5962V28.3975H30.8333V36.25H25.2562V23.9037L23.0033 26.4104H19.7691L15.8333 17.7145V36.25H8.52579Z"
              fill="currentColor"
            />
          </g>
        </svg>
      ),
    },
    {
      key: "Verheiratet, ohne Kinde",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="mask0_923_41152"
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="40"
            height="40"
          >
            <rect width="40" height="40" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_923_41152)">
            <path
              d="M13.5205 26.4792C13.1177 26.0764 12.9163 25.5834 12.9163 25C12.9163 24.4167 13.1177 23.9237 13.5205 23.5209C13.9232 23.1181 14.4163 22.9167 14.9996 22.9167C15.583 22.9167 16.076 23.1181 16.4788 23.5209C16.8816 23.9237 17.083 24.4167 17.083 25C17.083 25.5834 16.8816 26.0764 16.4788 26.4792C16.076 26.882 15.583 27.0834 14.9996 27.0834C14.4163 27.0834 13.9232 26.882 13.5205 26.4792ZM23.5205 26.4792C23.1177 26.0764 22.9163 25.5834 22.9163 25C22.9163 24.4167 23.1177 23.9237 23.5205 23.5209C23.9232 23.1181 24.4163 22.9167 24.9996 22.9167C25.583 22.9167 26.076 23.1181 26.4788 23.5209C26.8816 23.9237 27.083 24.4167 27.083 25C27.083 25.5834 26.8816 26.0764 26.4788 26.4792C26.076 26.882 25.583 27.0834 24.9996 27.0834C24.4163 27.0834 23.9232 26.882 23.5205 26.4792ZM19.9996 36.6667C23.7219 36.6667 26.8746 35.375 29.458 32.7917C32.0413 30.2084 33.333 27.0556 33.333 23.3334C33.333 22.6667 33.2913 22.0209 33.208 21.3959C33.1246 20.7709 32.9719 20.1667 32.7496 19.5834C32.1663 19.7223 31.583 19.8264 30.9996 19.8959C30.4163 19.9653 29.8052 20 29.1663 20C26.6385 20 24.2496 19.4584 21.9996 18.375C19.7496 17.2917 17.833 15.7778 16.2496 13.8334C15.3607 16 14.0899 17.882 12.4371 19.4792C10.7844 21.0764 8.86075 22.2778 6.6663 23.0834V23.3334C6.6663 27.0556 7.95797 30.2084 10.5413 32.7917C13.1246 35.375 16.2774 36.6667 19.9996 36.6667ZM16.9642 10.3688C18.227 12.6977 19.9787 14.4685 22.2192 15.6813C24.4595 16.8938 26.7752 17.5 29.1663 17.5C29.6194 17.5 30.0612 17.4766 30.4917 17.4296C30.9223 17.3827 31.3587 17.3153 31.8009 17.2275C30.6876 15.0162 29.0749 13.2587 26.963 11.955C24.8507 10.6517 22.5296 10 19.9996 10C19.4399 10 18.918 10.0316 18.4338 10.0946C17.9499 10.1577 17.4601 10.2491 16.9642 10.3688ZM7.13422 20.1509C8.70061 19.3667 10.1878 18.2209 11.5959 16.7134C13.0039 15.2059 13.9131 13.3987 14.3234 11.2917C12.5006 12.1187 10.9601 13.3073 9.70172 14.8575C8.44311 16.4078 7.58728 18.1723 7.13422 20.1509ZM5.49964 17.3209C6.19186 15.4745 7.2138 13.8328 8.56547 12.3959C9.91686 10.9589 11.4996 9.84088 13.3138 9.04171C12.7774 8.37282 12.1203 7.83115 11.3426 7.41671C10.5651 7.00227 9.70644 6.79504 8.76672 6.79504C7.14644 6.79504 5.777 7.35477 4.65839 8.47421C3.53977 9.59338 2.98047 10.9642 2.98047 12.5867C2.98047 13.5734 3.21172 14.4749 3.67422 15.2913C4.137 16.108 4.74547 16.7845 5.49964 17.3209ZM13.8271 37.9196C11.9002 37.0885 10.2241 35.9605 8.7988 34.5355C7.37353 33.1105 6.24505 31.4348 5.41339 29.5084C4.582 27.582 4.1663 25.5237 4.1663 23.3334C4.1663 22.7114 4.20325 22.095 4.27714 21.4842C4.35075 20.8731 4.46658 20.2866 4.62464 19.7246C3.37686 19.013 2.37422 18.0274 1.61672 16.768C0.859219 15.5088 0.480469 14.115 0.480469 12.5867C0.480469 10.2812 1.28602 8.32282 2.89714 6.71171C4.50825 5.1006 6.46658 4.29504 8.77214 4.29504C10.2124 4.29504 11.5373 4.63588 12.7467 5.31754C13.9559 5.99921 14.9355 6.92754 15.6855 8.10254C16.3649 7.90171 17.0687 7.75115 17.7967 7.65088C18.5245 7.55032 19.2588 7.50004 19.9996 7.50004C22.1899 7.50004 24.2482 7.9156 26.1746 8.74671C28.101 9.57782 29.7767 10.7057 31.2017 12.1305C32.6267 13.5552 33.7548 15.2306 34.5859 17.1567C35.4173 19.0828 35.833 21.1407 35.833 23.3305C35.833 25.5205 35.4174 27.5789 34.5863 29.5059C33.7552 31.4328 32.6273 33.1089 31.2026 34.5342C29.7778 35.9595 28.1024 37.088 26.1763 37.9196C24.2502 38.751 22.1923 39.1667 20.0026 39.1667C17.8126 39.1667 15.7541 38.751 13.8271 37.9196Z"
              fill="currentColor"
            />
          </g>
        </svg>
      ),
    },
    {
      key: "Verheiratet, mit Kindern",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 39 31"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.74375 16.4454C1.17958 15.424 0.747917 14.3861 0.44875 13.3317C0.149583 12.2772 0 11.2222 0 10.1667C0 7.31194 0.980833 4.90389 2.9425 2.9425C4.90389 0.980833 7.31194 0 10.1667 0C11.8847 0 13.5274 0.402778 15.0946 1.20833C16.6618 2.01389 18.0192 3.155 19.1667 4.63167C20.3142 3.155 21.6715 2.01389 23.2387 1.20833C24.806 0.402778 26.4486 0 28.1667 0C31.0214 0 33.4294 0.980833 35.3908 2.9425C37.3525 4.90389 38.3333 7.31194 38.3333 10.1667C38.3333 11.2008 38.1837 12.2399 37.8846 13.2838C37.5854 14.3274 37.1538 15.376 36.5896 16.4296C36.3501 16.051 36.0669 15.7122 35.74 15.4133C35.4133 15.1144 35.0469 14.8719 34.6408 14.6858C35.0297 13.8975 35.3257 13.131 35.5288 12.3863C35.7318 11.6415 35.8333 10.9017 35.8333 10.1667C35.8333 8.01056 35.0946 6.19375 33.6171 4.71625C32.1396 3.23875 30.3228 2.5 28.1667 2.5C26.1839 2.5 24.5401 3.06736 23.2354 4.20208C21.931 5.33653 20.5747 6.76917 19.1667 8.5C17.7586 6.75222 16.4024 5.31528 15.0979 4.18917C13.7932 3.06305 12.1494 2.5 10.1667 2.5C8.01056 2.5 6.19375 3.23875 4.71625 4.71625C3.23875 6.19375 2.5 8.01056 2.5 10.1667C2.5 10.9231 2.60153 11.6762 2.80458 12.4262C3.00764 13.1762 3.29819 13.9349 3.67625 14.7021C3.27042 14.8985 2.90722 15.1436 2.58667 15.4375C2.26611 15.7314 1.98514 16.0674 1.74375 16.4454ZM0 30.0321V28C0 26.8739 0.580972 25.9589 1.74292 25.255C2.90486 24.5508 4.41278 24.1988 6.26667 24.1988C6.60611 24.1988 6.93181 24.2083 7.24375 24.2275C7.55569 24.2467 7.85375 24.284 8.13792 24.3396C7.82375 24.8313 7.58542 25.3543 7.42292 25.9088C7.26069 26.4635 7.17958 27.0543 7.17958 27.6812V30.0321H0ZM10 30.0321V27.7404C10 26.1485 10.8471 24.8718 12.5412 23.9104C14.2354 22.9487 16.4454 22.4679 19.1712 22.4679C21.9224 22.4679 24.1372 22.9487 25.8158 23.9104C27.4942 24.8718 28.3333 26.1485 28.3333 27.7404V30.0321H10ZM31.1538 30.0321V27.6812C31.1538 27.0543 31.0796 26.4635 30.9312 25.9088C30.7826 25.3543 30.5599 24.8313 30.2629 24.3396C30.5471 24.284 30.8418 24.2467 31.1471 24.2275C31.4526 24.2083 31.7647 24.1988 32.0833 24.1988C33.9583 24.1988 35.4688 24.5508 36.6146 25.255C37.7604 25.9589 38.3333 26.8739 38.3333 28V30.0321H31.1538ZM19.1646 24.9679C17.4329 24.9679 15.9449 25.1976 14.7004 25.6571C13.4557 26.1165 12.765 26.6828 12.6283 27.3558V27.5321H25.7308V27.3558C25.5769 26.6828 24.8846 26.1165 23.6537 25.6571C22.4232 25.1976 20.9268 24.9679 19.1646 24.9679ZM6.26625 22.58C5.48181 22.58 4.81028 22.3007 4.25167 21.7421C3.69278 21.1835 3.41333 20.5119 3.41333 19.7275C3.41333 18.9328 3.69278 18.2614 4.25167 17.7133C4.81028 17.1653 5.48181 16.8913 6.26625 16.8913C7.06097 16.8913 7.735 17.1653 8.28833 17.7133C8.84194 18.2614 9.11875 18.9328 9.11875 19.7275C9.11875 20.5119 8.84194 21.1835 8.28833 21.7421C7.735 22.3007 7.06097 22.58 6.26625 22.58ZM32.0833 22.58C31.3056 22.58 30.6357 22.3007 30.0737 21.7421C29.5118 21.1835 29.2308 20.5119 29.2308 19.7275C29.2308 18.9328 29.5118 18.2614 30.0737 17.7133C30.6357 17.1653 31.3065 16.8913 32.0863 16.8913C32.8899 16.8913 33.5657 17.1653 34.1138 17.7133C34.6618 18.2614 34.9358 18.9328 34.9358 19.7275C34.9358 20.5119 34.6625 21.1835 34.1158 21.7421C33.5692 22.3007 32.8917 22.58 32.0833 22.58ZM19.1725 21.2179C17.9719 21.2179 16.9497 20.7972 16.1058 19.9558C15.2619 19.1147 14.84 18.0932 14.84 16.8913C14.84 15.6651 15.2606 14.6375 16.1017 13.8083C16.9431 12.9789 17.9647 12.5642 19.1667 12.5642C20.3925 12.5642 21.4201 12.9783 22.2496 13.8067C23.0787 14.6347 23.4933 15.661 23.4933 16.8854C23.4933 18.0857 23.0793 19.1079 22.2513 19.9521C21.4229 20.796 20.3967 21.2179 19.1725 21.2179ZM19.1667 15.0642C18.6625 15.0642 18.2319 15.2393 17.875 15.5896C17.5181 15.9396 17.3396 16.3735 17.3396 16.8913C17.3396 17.3954 17.5181 17.826 17.875 18.1829C18.2319 18.5396 18.6651 18.7179 19.1746 18.7179C19.6843 18.7179 20.1149 18.5396 20.4662 18.1829C20.8179 17.826 20.9937 17.3926 20.9937 16.8829C20.9937 16.3735 20.8186 15.9429 20.4683 15.5913C20.1181 15.2399 19.6842 15.0642 19.1667 15.0642Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      key: "In eingetragener Partnerschaft",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="mask0_904_41062"
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="40"
            height="40"
          >
            <rect width="40" height="40" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_904_41062)">
            <path
              d="M19.6475 33.2853C19.8119 33.2853 19.9792 33.2469 20.1492 33.1699C20.3189 33.093 20.4529 33.0053 20.5512 32.907L33.8812 19.577C34.2574 19.2009 34.5405 18.7991 34.7308 18.3716C34.9208 17.9444 35.0158 17.4958 35.0158 17.0258C35.0158 16.5385 34.9208 16.0695 34.7308 15.6187C34.5405 15.1676 34.2574 14.7626 33.8812 14.4037L27.2146 7.73701C26.8557 7.3609 26.4668 7.08576 26.0479 6.91159C25.6293 6.7377 25.1764 6.65076 24.6892 6.65076C24.2192 6.65076 23.7678 6.7377 23.335 6.91159C22.9022 7.08576 22.5032 7.3609 22.1379 7.73701L21.1825 8.69242L24.2658 11.8012C24.6397 12.1581 24.916 12.5652 25.0946 13.0224C25.2729 13.4796 25.3621 13.954 25.3621 14.4453C25.3621 15.4626 25.0224 16.3109 24.3429 16.9903C23.6635 17.6698 22.8151 18.0095 21.7979 18.0095C21.3065 18.0095 20.8305 17.9283 20.37 17.7658C19.9097 17.6035 19.5011 17.344 19.1442 16.987L15.9871 13.8558L8.74374 21.0991C8.61763 21.2252 8.52304 21.3663 8.45999 21.5224C8.39693 21.6783 8.36541 21.8385 8.36541 22.0033C8.36541 22.3108 8.47013 22.5735 8.67957 22.7916C8.88902 23.0096 9.14749 23.1187 9.45499 23.1187C9.61971 23.1187 9.78693 23.0802 9.95666 23.0033C10.1267 22.9263 10.2608 22.8387 10.3592 22.7403L15.8333 17.2662L17.5896 19.0224L12.1408 24.4966C12.015 24.6227 11.9205 24.7638 11.8575 24.9199C11.7944 25.0758 11.7629 25.236 11.7629 25.4008C11.7629 25.6977 11.8703 25.9535 12.085 26.1683C12.2997 26.383 12.5555 26.4903 12.8525 26.4903C13.0172 26.4903 13.1844 26.4519 13.3542 26.3749C13.5242 26.298 13.6582 26.2103 13.7562 26.112L19.4229 20.4712L21.1796 22.2274L15.5383 27.8941C15.423 27.9924 15.3312 28.1265 15.2629 28.2962C15.1946 28.4662 15.1604 28.6334 15.1604 28.7978C15.1604 29.0951 15.2678 29.351 15.4825 29.5658C15.6972 29.7805 15.953 29.8878 16.25 29.8878C16.4144 29.8878 16.5747 29.8563 16.7308 29.7933C16.8867 29.7302 17.0276 29.6356 17.1537 29.5095L22.8204 23.8687L24.5771 25.6249L18.9104 31.2916C18.7843 31.4177 18.6897 31.5641 18.6267 31.7308C18.5636 31.8974 18.5321 32.0577 18.5321 32.2116C18.5321 32.5191 18.6464 32.7749 18.875 32.9791C19.1036 33.1833 19.3611 33.2853 19.6475 33.2853ZM19.6217 35.7849C18.6794 35.7849 17.8579 35.4581 17.1571 34.8045C16.4562 34.1506 16.0899 33.3365 16.0579 32.362C15.1135 32.2978 14.3244 31.9624 13.6908 31.3558C13.0572 30.7488 12.7137 29.9517 12.6604 28.9645C11.6732 28.9006 10.8751 28.5556 10.2662 27.9295C9.65707 27.3034 9.3311 26.5159 9.28832 25.567C8.29693 25.5031 7.4786 25.1437 6.83332 24.4887C6.18804 23.8337 5.86541 23.0052 5.86541 22.0033C5.86541 21.5119 5.95888 21.0305 6.14582 20.5591C6.33277 20.088 6.60471 19.6741 6.96166 19.3174L15.9871 10.3174L20.8687 15.1987C20.9668 15.314 21.0954 15.4059 21.2546 15.4745C21.414 15.5428 21.5867 15.577 21.7725 15.577C22.0758 15.577 22.3375 15.4766 22.5575 15.2758C22.7778 15.0749 22.8879 14.812 22.8879 14.487C22.8879 14.3012 22.8537 14.1287 22.7854 13.9695C22.7168 13.8103 22.6248 13.6816 22.5096 13.5833L16.6633 7.73701C16.3044 7.3609 15.9129 7.08576 15.4887 6.91159C15.0646 6.7377 14.6089 6.65076 14.1217 6.65076C13.6517 6.65076 13.2057 6.7377 12.7837 6.91159C12.3615 7.08576 11.9625 7.3609 11.5867 7.73701L6.11207 13.237C5.80874 13.5403 5.56041 13.8988 5.36707 14.3124C5.17374 14.7258 5.05999 15.1473 5.02582 15.577C4.99138 15.9317 5.00735 16.2827 5.07374 16.6299C5.13985 16.9771 5.25624 17.3035 5.42291 17.6091L3.58332 19.4487C3.20721 18.9059 2.92416 18.298 2.73416 17.6249C2.54388 16.9519 2.46582 16.2692 2.49999 15.577C2.53416 14.8098 2.70721 14.0688 3.01916 13.3541C3.3311 12.6394 3.76804 12.001 4.32999 11.4391L9.78832 5.98076C10.4125 5.37381 11.0915 4.91714 11.8254 4.61076C12.5593 4.30409 13.3301 4.15076 14.1379 4.15076C14.9454 4.15076 15.7135 4.30409 16.4421 4.61076C17.171 4.91714 17.8387 5.37381 18.4454 5.98076L19.4008 6.93576L20.3558 5.98076C20.9797 5.37381 21.656 4.91714 22.3846 4.61076C23.1132 4.30409 23.8814 4.15076 24.6892 4.15076C25.4969 4.15076 26.2678 4.30409 27.0017 4.61076C27.7355 4.91714 28.406 5.37381 29.0129 5.98076L35.6379 12.6058C36.2446 13.2127 36.7093 13.9034 37.0321 14.6778C37.3546 15.4523 37.5158 16.2434 37.5158 17.0512C37.5158 17.859 37.3546 18.6271 37.0321 19.3558C36.7093 20.0844 36.2446 20.752 35.6379 21.3587L22.3075 34.6633C21.94 35.0308 21.5261 35.3096 21.0658 35.4999C20.6053 35.6899 20.1239 35.7849 19.6217 35.7849Z"
              fill="currentColor"
            />
          </g>
        </svg>
      ),
    },
    {
      key: "Verwitwet / Geschieden",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 37 35"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18.4763 29.1667C22.1986 29.1667 25.3513 27.8681 27.9347 25.2708C30.518 22.6736 31.8097 19.5278 31.8097 15.8333C31.8097 14.8653 31.7162 13.94 31.5293 13.0575C31.3423 12.175 31.064 11.3321 30.6943 10.5288C29.987 10.9796 29.2355 11.3247 28.4397 11.5642C27.6436 11.8033 26.8225 11.9229 25.9763 11.9229C24.4336 11.9229 22.9998 11.5506 21.6751 10.8058C20.3504 10.0614 19.2841 9.02667 18.4763 7.70167C17.6794 9.02667 16.6158 10.0614 15.2855 10.8058C13.9555 11.5506 12.5191 11.9229 10.9763 11.9229C10.1302 11.9229 9.3119 11.8033 8.52134 11.5642C7.73079 11.3247 6.98176 10.985 6.27426 10.545C5.90482 11.3589 5.62384 12.2071 5.43134 13.0896C5.23912 13.9721 5.14301 14.8867 5.14301 15.8333C5.14301 19.5278 6.44162 22.6736 9.03884 25.2708C11.6361 27.8681 14.7819 29.1667 18.4763 29.1667ZM14.9555 18.9792C15.3583 18.5764 15.5597 18.0833 15.5597 17.5C15.5597 16.9167 15.3583 16.4236 14.9555 16.0208C14.5527 15.6181 14.0597 15.4167 13.4763 15.4167C12.893 15.4167 12.4 15.6181 11.9972 16.0208C11.5944 16.4236 11.393 16.9167 11.393 17.5C11.393 18.0833 11.5944 18.5764 11.9972 18.9792C12.4 19.3819 12.893 19.5833 13.4763 19.5833C14.0597 19.5833 14.5527 19.3819 14.9555 18.9792ZM24.9555 18.9792C25.3583 18.5764 25.5597 18.0833 25.5597 17.5C25.5597 16.9167 25.3583 16.4236 24.9555 16.0208C24.5527 15.6181 24.0597 15.4167 23.4763 15.4167C22.893 15.4167 22.4 15.6181 21.9972 16.0208C21.5944 16.4236 21.393 16.9167 21.393 17.5C21.393 18.0833 21.5944 18.5764 21.9972 18.9792C22.4 19.3819 22.893 19.5833 23.4763 19.5833C24.0597 19.5833 24.5527 19.3819 24.9555 18.9792ZM10.9763 9.42292C12.705 9.42292 14.1788 8.81347 15.398 7.59458C16.6169 6.37542 17.2263 4.90153 17.2263 3.17292V2.55125C15.22 2.73069 13.3716 3.33597 11.6813 4.36708C9.99134 5.39792 8.59718 6.71681 7.49884 8.32375C8.02023 8.66764 8.57204 8.93681 9.15426 9.13125C9.73648 9.3257 10.3438 9.42292 10.9763 9.42292ZM25.9763 9.42292C26.6088 9.42292 27.2188 9.32736 27.8063 9.13625C28.3941 8.94514 28.9487 8.66903 29.4701 8.30792C28.3823 6.71153 26.9976 5.41125 25.3159 4.40708C23.6343 3.40292 21.7711 2.78972 19.7263 2.5675V3.17292C19.7263 4.90153 20.3358 6.37542 21.5547 7.59458C22.7738 8.81347 24.2477 9.42292 25.9763 9.42292ZM3.02468 35C2.14857 35 1.40926 34.6736 0.806761 34.0208C0.203983 33.3681 -0.0610172 32.6036 0.0117606 31.7275L1.46343 15.83C1.67509 13.6142 2.2644 11.5406 3.23134 9.60917C4.19801 7.6775 5.44051 6.00069 6.95884 4.57875C8.4769 3.15653 10.2205 2.03792 12.1897 1.22292C14.1586 0.407638 16.2541 0 18.4763 0C20.6986 0 22.7941 0.407638 24.763 1.22292C26.7322 2.03792 28.4758 3.15653 29.9938 4.57875C31.5122 6.00069 32.7547 7.6775 33.7213 9.60917C34.6883 11.5406 35.2776 13.6142 35.4893 15.83L36.9409 31.7275C37.0137 32.6036 36.7487 33.3681 36.1459 34.0208C35.5434 34.6736 34.8041 35 33.928 35H3.02468ZM18.4763 31.6667C14.9722 31.6667 11.8552 30.64 9.12551 28.5867C6.39579 26.5331 4.52343 23.9283 3.50843 20.7725L2.51176 31.9392C2.49037 32.0886 2.53301 32.2194 2.63968 32.3317C2.74662 32.4439 2.87496 32.5 3.02468 32.5H33.928C34.0777 32.5 34.2061 32.4439 34.313 32.3317C34.4197 32.2194 34.4623 32.0886 34.4409 31.9392L33.4443 20.7883C32.4293 23.9444 30.5612 26.5465 27.8401 28.5946C25.119 30.6426 21.9977 31.6667 18.4763 31.6667Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
  ];
  const cantonOptions = [
    "Aargau",
    "Appenzell Ausserrhoden",
    "Appenzell Innerrhoden",
    "Basel-Landschaft",
    "Basel-Stadt",
    "Bern",
    "Fribourg",
    "Geneva",
    "Glarus",
    "Graubünden",
    "Jura",
    "Lucerne",
    "Neuchâtel",
    "Nidwalden",
    "Obwalden",
    "Schaffhausen",
    "Schwyz",
    "Solothurn",
    "St. Gallen",
    "Thurgau",
    "Ticino",
    "Uri",
    "Valais",
    "Vaud",
    "Zug",
    "Zurich",
  ];
  const pillarsOption = [
    "Säule 3a (gebundene Vorsorge)",
    "Säule 3b (freie Vorsorge)",
    "Pensionskasse (BVG) freiwillig aufgestockt",
    "Keine Einzahlungen getätigt",
  ];
  const stepIcons = [
    SaveMoney,
    FamilyIcon,
    LocationIcon,
    DonationBox,
    CallIcon,
  ];
// Yeh helpers component ke andar define karo
const today = new Date();

// Minimum allowed DOB (max age = 60)
// const minDate = new Date(
//   today.getFullYear() - 60,
//   today.getMonth(),
//   today.getDate()
// ).toISOString().split("T")[0];

const minDate = "1900-01-01";

// Maximum allowed DOB (min age = 18)
const maxDate = new Date(
  today.getFullYear() - 18,
  today.getMonth(),
  today.getDate()
).toISOString().split("T")[0];
  const isActive = (s) => currentStep === s;
  const isCompleted = (s) => currentStep > s;
  const optBtn = (sel) =>
    `px-4 py-3 rounded-xl border text-sm text-[#475569] font-medium transition-all text-left ${
      sel
        ? "bg-[#DBF1FF] border-[#036EA4]"
        : "bg-white hover:bg-blue-50/50 border-gray-100"
    }`;

  /* ── render ─────────────────────────────────────────────────────── */
  return (
    <>
      {/* ══ SECTION with BG pattern ══════════════════════════════════ */}
     <section
        id="formular"
      className="px-4 py-12 sm:px-6 sm:py-16 md:px-10 md:py-20 lg:px-8 bg-[#F0F9FF] max-w-[1536px] mx-auto shadow-lg">
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#181818] mb-3 text-center">
       {t("Start in 2 minutes")}
      </h2>

      <p className="text-[#475569] text-sm sm:text-base md:text-lg text-center max-w-2xl mx-auto">
        {t("We only ask what's needed to prepare your tax filing.")}
      </p>
    </section>
      <section
        id="questionnaire"
        className="w-full relative overflow-hidden bg-[#F0F9FF] max-w-[1536px] mx-auto mt-4"
        style={{
          backgroundImage: "url('/images/frameP.png')",
          backgroundRepeat: "repeat",
          backgroundSize: "cover",
        }}
      >
        <div className="relative z-10 max-w-4xl mx-auto px-4 py-12 md:px-8 md:py-16">
          <AnimatePresence mode="wait">
            {/* ── BANNER ──────────────────────────────────────────── */}
            {!isStarted ? (
              <motion.div
                key="banner"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-white/80 backdrop-blur-sm rounded-2xl px-8 py-10 shadow-xl border border-white"
              >
                <div>
                  <h2 className="text-3xl font-bold text-[#181818]">
                    {t("Start in 2 minutes")}
                  </h2>
                  <p className="text-[#475569]">
                    {t("We only ask what's needed to prepare your tax filing.")}
                  </p>
                </div>
                <button
                  onClick={() => setIsStarted(true)}
                  className="flex items-center gap-2 bg-[#036EA4] text-white px-4 py-2 rounded-xl font-bold btn-hover-animate"
                >
                  {t("Start Questionnaire")} <ArrowRight size={20} />
                </button>
              </motion.div>
            ) : (
              /* ── QUIZ ───────────────────────────────────────────── */
              <motion.div
                key="quiz"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              >
                {/* header */}
                {/* <div className="text-center mb-10">
                  <h2 className="text-3xl md:text-4xl font-black text-[#181818] mb-2">
                    {t("Start in 2 minutes")}
                  </h2>
                  <p className="text-[#475569]">
                    {t("We only ask what's needed to prepare your tax filing.")}
                  </p>
                </div> */}

                {/* ════ STEPS ══════════════════════════════════════ */}
                {/* <div className="flex flex-col"> */}
                <div
                  className="flex flex-col space-y-6 relative"
                  ref={containerRef}
                >
                  <ProgressLine
                    currentStep={currentStep}
                    circleRefs={circleRefs}
                    containerRef={containerRef}
                  />
                  {/* ── 1: Income ─────────────────────────────────── */}
                  <div ref={sectionRefs[1]}>
                    <StepRow
                      stepId={1}
                      isActive={isActive(1)}
                      isCompleted={isCompleted(1)}
                      icon={stepIcons[0]}
                      showIcon={showIcon}
                      animateIn={false}
                      circleRef={circleRefs[1]}
                    >
                      <h3 className="text-xl font-bold text-[#181818] mb-1">
                        {t("Wie hoch ist Ihr ")}
                      </h3>
                      <p className="text-[#475569] text-sm mb-5">
                        {t("Select your approximate annual income range.")}
                      </p>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {incomeOptions.map((opt) => (
                          <button
                            key={opt}
                            onClick={() =>
                              setFormData({ ...formData, incomeRange: opt })
                            }
                            className={optBtn(formData.incomeRange === opt)}
                            style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                          >
                            {t(opt)}
                          </button>
                        ))}
                      </div>
                      <NextBtn
                        label={t("nextQuestion")}
                        disabled={!formData.incomeRange}
                        onClick={() => {
                          setFirstStep(true);
                          setCurrentStep(2);
                        }}
                      />
                    </StepRow>
                  </div>

                  {/* connector 1 → 2 (always rendered once quiz started; fills on firstStep) */}
                  {/* <ConnectorRow filled={firstStep} height={260} /> */}

                  {/* ── 2: Living ─────────────────────────────────── */}
                  <AnimatePresence>
                    {firstStep && (
                      <div ref={sectionRefs[2]}>
                        <StepRow
                          stepId={2}
                          isActive={isActive(2)}
                          isCompleted={isCompleted(2)}
                          icon={stepIcons[1]}
                          showIcon={showIcon}
                          animateIn
                          circleRef={circleRefs[2]}
                        >
                          <h3 className="text-xl font-bold text-[#181818] mb-1">
                            {t("Wie sieht Ihre aktuel")}
                          </h3>
                          <p className="text-[#475569] text-sm mb-5">
                            {t("Tell us about your household.")}
                          </p>
                          <div className="grid grid-cols-2 gap-3 md:grid-cols-2">
                            {livingOptions.map(({ key, icon }) => {
                              const selected = formData.livingSituation === key;
                              return (
                                <button
                                  key={key}
                                  onClick={() =>
                                    setFormData({
                                      ...formData,
                                      livingSituation: key,
                                    })
                                  }
                                  className="flex flex-col items-start gap-2 p-4 rounded-xl border transition-all text-left"
                                  style={{
                                    background: selected ? "#DBF1FF" : "white",
                                    borderColor: selected
                                      ? "#036EA4"
                                      : "transparent",
                                    boxShadow: selected
                                      ? "0 4px 16px rgba(3,110,164,0.15)"
                                      : "0 2px 8px rgba(0,0,0,0.05)",
                                    color: selected ? "#036EA4" : "#94A3B8",
                                  }}
                                >
                                  {/* icon */}
                                  <div
                                    className="p-2 rounded-lg transition-all"
                                    style={{
                                      background: selected
                                        ? "linear-gradient(139.34deg, #036EA4 12.34%, #06A7CA 88.3%)"
                                        : "#E8E8E8",
                                      color: selected ? "white" : "#6F6F6F",
                                    }}
                                  >
                                    {icon}
                                  </div>
                                  {/* label */}
                                  <span
                                    className="text-sm font-medium leading-tight"
                                    style={{
                                      color: selected ? "#036EA4" : "#6F6F6F",
                                      fontWeight: selected ? "600" : "400",
                                    }}
                                  >
                                    {t(key)}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          <NextBtn
                            label={t("nextQuestion")}
                            disabled={!formData.livingSituation}
                            onClick={() => {
                              setSecondStep(true);
                              setCurrentStep(3);
                            }}
                          />
                        </StepRow>
                      </div>
                    )}
                  </AnimatePresence>

                  {/* connector 2 → 3 */}
                  {/* {firstStep && (
                    <ConnectorRow filled={secondStep} height={320} />
                  )} */}

                  {/* ── 3: Canton ─────────────────────────────────── */}
                  <AnimatePresence>
                    {firstStep && secondStep && (
                      <div ref={sectionRefs[3]}>
                        <StepRow
                          stepId={3}
                          isActive={isActive(3)}
                          isCompleted={isCompleted(3)}
                          icon={stepIcons[2]}
                          showIcon={showIcon}
                          animateIn
                          circleRef={circleRefs[3]}
                        >
                                                     {/* badge */}
                          
                          <h3 className="text-xl font-bold text-[#181818] mb-1">
                            {t("3- Your Canton")}
                          </h3>
                          <p className="text-[#475569] text-sm mb-5">
                            {t("Select the canton where you are registered")}
                          </p>
                          <CustomDropdown
                            options={cantonOptions}
                            value={formData?.canton || ""}
                            t={t}
                            placeholder={t("Select your canton")}
                            onChange={(v) =>
                              setFormData({ ...formData, canton: v })
                            }
                          />
                          <NextBtn
                            label={t("nextQuestion")}
                            disabled={!formData.canton}
                            onClick={() => {
                              setThirdStep(true);
                              setCurrentStep(4);
                            }}
                          />
                        </StepRow>
                      </div>
                    )}
                  </AnimatePresence>

                  {/* connector 3 → 4 */}
                  {/* {firstStep && secondStep && (
                    <ConnectorRow filled={thirdStep} height={180} />
                  )} */}

                  {/* ── 4: Pillar 3A ──────────────────────────────── */}
                  <AnimatePresence>
                    {firstStep && secondStep && thirdStep && (
                      <div ref={sectionRefs[4]}>
                        <StepRow
                          stepId={4}
                          isActive={isActive(4)}
                          isCompleted={isCompleted(4)}
                          icon={stepIcons[3]}
                          showIcon={showIcon}
                          animateIn
                          circleRef={circleRefs[4]}
                        >
                           {/* badge */}
                           <div className="inline-flex items-center gap-2 bg-[#EBE8FF] border border-[#6418C7] rounded-lg px-3 py-1 mb-4">
                            <span className="text-xs font-semibold text-[#6418C7]">
                              {t("Tax Deduction Tip")}
                            </span>
                          </div>
                          
                          <h3 className="text-xl font-bold text-[#181818] mb-1">
                             4 - {t("Haben Sie im letzten Steuerjahr")}
                          </h3>
                          <p className="text-[#475569] text-sm mb-5">
                            {t(
                              "Do you have a Pillar 3a retirement savings account?",
                            )}
                          </p>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            {pillarsOption.map((opt) => (
                              <label
                                key={opt}
                                onClick={() =>
                                  setFormData({ ...formData, pillar3a: opt })
                                }
                                className="flex items-center gap-3 cursor-pointer group"
                              >
                                <div
                                  className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                                    formData.pillar3a === opt
                                      ? "bg-[#036EA4] border-[#036EA4]"
                                      : "bg-white border-[#036EA4] group-hover:border-blue-400"
                                  }`}
                                >
                                  {formData.pillar3a === opt && (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3.5 w-3.5 text-white"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  )}
                                </div>
                                <span className="text-sm text-[#475569]">
                                  {t(opt)}
                                </span>
                              </label>
                            ))}
                          </div>
                          <NextBtn
                            label={t("nextQuestion")}
                            disabled={!formData.pillar3a}
                            onClick={() => {
                              setFourthStep(true);
                              setCurrentStep(5);
                            }}
                          />
                        </StepRow>
                      </div>
                    )}
                  </AnimatePresence>

                  {/* connector 4 → 5 */}
                  {/* {firstStep && secondStep && thirdStep && (
                    <ConnectorRow filled={fourthStep} height={260} />
                  )} */}

                  {/* ── 5: Contact ────────────────────────────────── */}
                  <AnimatePresence>
                    {firstStep && secondStep && thirdStep && fourthStep && (
                      <div ref={sectionRefs[5]}>
                        <StepRow
                          stepId={5}
                          isActive={isActive(5)}
                          isCompleted={isCompleted(5)}
                          icon={stepIcons[4]}
                          showIcon={showIcon}
                          animateIn
                          circleRef={circleRefs[5]}
                        >
                          {/* badge */}
                            {/*  <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 mb-4">
                         <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                            <span className="text-xs font-semibold text-amber-700">
                              {t("Almost there!")}
                            </span> 
                          </div>*/}
                          <div className="inline-flex items-center gap-2 bg-[#DAFAE5] border border-[#016630] rounded-lg px-3 py-1 mb-4">
                            <span className="text-xs font-semibold text-[#016630]">
                            {t("Investment Planning")}
                            </span>
                          </div>

                          <h3 className="text-xl font-bold text-[#181818] mb-1">
                             5 - {t("Wie dürfen wir Sie erreichen?")}
                          </h3>
                          <p className="text-[#475569] text-sm mb-6">
                            {t(
                              "Please provide your details so we can send you the results.",
                            )}
                          </p>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-bold uppercase tracking-wide text-[#181818]">
                                {t("Full Name")}
                              </label>
                              <input
                                type="text"
                                placeholder={t("fullName_placeholder")}
                                value={formData.contact.name}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    contact: {
                                      ...formData.contact,
                                      name: e.target.value,
                                    },
                                  })
                                }
                                onBlur={() =>
                                  setErrors({
                                    ...errors,
                                    name: validateName(formData.contact.name),
                                  })
                                }
                                className="p-3 rounded-xl bg-white focus:border-[#007AB7] outline-none transition-all text-sm"
                                style={{
                                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                                }}
                              />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-bold uppercase tracking-wide text-[#181818]">
                                {t("Phone Number")}
                              </label>
                              <input
                                type="tel"
                                placeholder={t("phoneNumber_placeholder")}
                                maxLength={15}
                                value={formData.contact.phone}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    contact: {
                                      ...formData.contact,
                                      phone: e.target.value,
                                    },
                                  })
                                }
                                onBlur={() =>
                                  setErrors({
                                    ...errors,
                                    phone: validatePhone(
                                      formData.contact.phone,
                                    ),
                                  })
                                }
                                className="p-3 rounded-xl bg-white focus:border-[#007AB7] outline-none transition-all text-sm"
                                style={{
                                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                                }}
                              />
                            </div>
                            
      <div className="flex flex-col gap-1.5">
  <label className="text-xs font-bold uppercase tracking-wide text-[#181818]">
    {t("Date of Birth")}
  </label>

  <div className="relative">
    {/* ✅ Hidden input — right side pe bottom mein */}
    <input
      type="date"
      id="birthDateHidden"
      min={minDate}
      max={maxDate}
      value={formData.contact.birthDate}
      onChange={(e) => {
        const val = e.target.value;
        if (val && val > maxDate) {
          setErrors({ ...errors, birthDate: t("You must be at least 18 years old") });
        } else if (val && val < minDate) {
          setErrors({ ...errors, birthDate: t("Persons over 60 are not eligible for insurance policy changes") });
        } else {
          setErrors({ ...errors, birthDate: null });
        }
        setFormData({ ...formData, contact: { ...formData.contact, birthDate: val } });
      }}
      style={{
        position: "absolute",
        bottom: 0,
        right: 0,
        left:100,       // ✅ right side
        width: "1px",
        height: "1px",
        opacity: 0,
        pointerEvents: "none",
      }}
      tabIndex={-1}
    />

    {/* Visible div */}
    <div
      onClick={() => document.getElementById("birthDateHidden")?.showPicker()}
      className="flex items-center justify-between p-3 rounded-xl bg-white cursor-pointer text-sm"
      style={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}
    >
      <span style={{ color: formData.contact.birthDate ? "#181818" : "#9CA3AF" }}>
        {formData.contact.birthDate
          ? i18n.language === "de"
            ? formData.contact.birthDate.split("-").reverse().join(".")
            : formData.contact.birthDate.split("-").reverse().join("/")
          : i18n.language === "de" ? "TT.MM.JJJJ" : "DD/MM/YYYY"
        }
      </span>
    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#181818]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  </div>

  {errors.birthDate && (
    <span className="text-red-500 text-xs mt-1">{errors.birthDate}</span>
  )}
</div>
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-bold uppercase tracking-wide text-[#181818]">
                                {t("Email Address")}
                              </label>
                              <input
                                type="email"
                                placeholder={t("emailAddress_placeholder")}
                                value={formData.contact.email}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    contact: {
                                      ...formData.contact,
                                      email: e.target.value,
                                    },
                                  })
                                }
                                onBlur={() =>
                                  setErrors({
                                    ...errors,
                                    email: validateEmail(
                                      formData.contact.email,
                                    ),
                                  })
                                }
                                className="p-3  rounded-xl bg-white focus:border-[#007AB7] outline-none transition-all text-sm"
                                style={{
                                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                                }}
                              />
                            </div>
                          </div>

                          <div className="flex items-start gap-3 pb-4">
                            <input
                              type="checkbox"
                              id="terms"
                              className="w-5 h-5 mt-0.5 cursor-pointer flex-shrink-0 rounded"
                              style={{
                                border: "2px solid #036EA4",
                                appearance: "none",
                                WebkitAppearance: "none",
                                backgroundColor: formData.termsAccepted
                                  ? "#036EA4"
                                  : "white",
                                backgroundImage: formData.termsAccepted
                                  ? `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3E%3C/svg%3E")`
                                  : "none",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",
                                backgroundSize: "100%",
                              }}
                              checked={formData.termsAccepted}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  termsAccepted: e.target.checked,
                                })
                              }
                            />
                            <label
                              htmlFor="terms"
                              className="text-sm leading-snug text-[#475569] cursor-pointer select-none"
                            >
                              {t("I agree to the")}
                            </label>
                          </div>

                          <div className="flex justify-end pt-1">
                            <button
                              onClick={handleSubmit}
                              disabled={!isStep5Valid()}
                              className="w-full md:w-auto bg-[#007AB7] hover:bg-[#005f8d] text-white px-8 py-2.5 rounded-lg font-bold transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed btn-hover-animate flex items-center justify-center gap-2"
                            >
                              {t("Submit")} <ArrowRight size={16} />
                            </button>
                          </div>
                        </StepRow>
                      </div>
                    )}
                  </AnimatePresence>
                </div>
                {/* end steps */}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ── SUCCESS POPUP ─────────────────────────────────────────── */}
      <AnimatePresence>
        {isSubmitted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full text-center"
            >
              <div
                style={{
                  background:
                    "linear-gradient(139.34deg,#036EA4 12.34%,#06A7CA 88.3%)",
                }}
                className="flex items-center justify-center w-14 h-14 mx-auto mb-5 rounded-full"
              >
                <Check size={28} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#181818] mb-2 font-montserrat">
                {t("thankYouMsg")}
              </h2>
              <p className="text-lg text-[#475569] mb-5">
                {t("popUpDescription")}
              </p>
              <div className="flex items-start gap-3 bg-[#F0F9FF] rounded-xl px-3 py-3 mb-6 text-left">
                <div className="shrink-0">
                  <div className="w-9 h-9 rounded-md bg-[#036EA4] flex items-center justify-center">
                    <img src="/images/chat_info.svg" alt="" />
                  </div>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed">
                  {t("popUpContent")}{" "}
                  <span className="font-semibold">
                    {t("free of charge and carries no obligation.")}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setIsSubmitted(false)}
                className="bg-[#036EA4] hover:bg-[#005f8d] text-white px-6 py-1.5 rounded-lg font-semibold transition-all active:scale-95"
              >
                {t("Close")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}